/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  PanoramaShell,
  type PanoramaHotspot,
  type PannellumViewer as PannellumViewerHandle,
} from '@ironview/interactive-viewer';
import { DialogTitle, DialogDescription } from '@/shared/ui/dialog';
import { DominantTrade, Photo360Response, PhotoMarker } from '../types/types';
import FABMenu from './FABMenu';
import HotspotDialog from './HotspotDialog';
import {
  fromViewerPanoramaHotspot,
  toViewerPanoramaHotspots,
} from '../adapters/ironview360Adapter';
import { renderIronview360PanoramaHotspot } from '../adapters/ironview360PanoramaRenderer';

import 'pannellum/build/pannellum.css';
import 'pannellum/build/pannellum.js';
import '@ironview/interactive-viewer/styles.css';
import '../styles/HotspotStyles.css';

import {
  PITCH_OFFSET,
  PANORAMA_NAV_BUTTON_BOX,
  PANORAMA_NAV_BUTTON_ICON,
} from '../constants/constants';
import LayoutMapPreview from './LayoutMapPreview';

const LOG = '[Ironview360Panorama]';

// Type definitions for hotspot system
interface HotspotObject {
  id: number | string;
  type: 'note' | 'image' | 'label';
  title: string;
  created_at: string;
  updated_at: string;
  dominant_trade?: DominantTrade | null;
}

interface Hotspot {
  id: number;
  pitch: number;
  yaw: number;
  hfov: number;
  overlay_count: number;
  created_by: string | null;
  title?: string;
  objects: HotspotObject[];
  photo_id: number;
  created_at: string;
  updated_at: string;
}

interface LegacyHotspot {
  id: number;
  pitch: number;
  yaw: number;
  title?: string;
}

interface HotspotsData {
  hotspots: (Hotspot | LegacyHotspot)[];
}

// Photo data for navigation (from PanoramaViewerSimple)
interface Photo360Data {
  image_url?: string;
  photo_id: number;
  photo_name: string;
  date: string;
  prev_photo?: number | null;
  next_photo?: number | null;
  prev_date?: string | null;
  next_date?: string | null;
  prev_date_photo_id?: number | null;
  next_date_photo_id?: number | null;
}

/**
 * Ironview 360 panorama controls and object overlays.
 *
 * Pannellum lifecycle is shared; this component owns only Ironview 360
 * navigation, construction-object controls, map context, and dialogs.
 */
interface Ironview360PanoramaExperienceProps {
  // Required props
  photoId: number;
  imageUrl: string;

  // Host data
  photoData?: Photo360Response | Photo360Data | null;
  hotspotsData?: HotspotsData | null;
  projectId?: string;

  // Callbacks
  onNavigateToPhotoId?: (photoId: number) => void;
  onHotspotCreated?: () => Promise<void>;
  onPanoramaError?: () => Promise<void>;

  // Target navigation (for navigating to specific hotspot)
  targetCoordinates?: { pitch: number; yaw: number } | null;
  onTargetReached?: () => void;

  // Share feature - layout context for location preview
  markerNumber?: number;
  layoutImageUrl?: string;
  layoutFlippedHorizontal?: boolean;
  layoutFlippedVertical?: boolean;
  layoutImageDimensions?: { width: number; height: number };
  photoMarkers?: PhotoMarker[];
}

const Ironview360PanoramaExperience: React.FC<Ironview360PanoramaExperienceProps> = ({
  // Required
  photoId,
  imageUrl,

  // Data props
  photoData,
  hotspotsData,
  projectId,

  // Callbacks
  onNavigateToPhotoId,
  onHotspotCreated,
  onPanoramaError,

  // Target navigation
  targetCoordinates,
  onTargetReached,

  // Share feature
  markerNumber,
  layoutImageUrl,
  layoutFlippedHorizontal = false,
  layoutFlippedVertical = false,
  layoutImageDimensions,
  photoMarkers,
}) => {
  // Extract photo navigation data (supports both Photo360Response and Photo360Data)
  const photoName = (photoData as any)?.photo_name || `Photo ${photoId}`;
  const photoDate = (photoData as any)?.date || '';
  const layoutName = (photoData as any)?.layout_name || 'Current Layout';
  const layoutType = (photoData as any)?.layout_type || '';
  const prevPhotoId = (photoData as any)?.prev_photo;
  const nextPhotoId = (photoData as any)?.next_photo;
  const prevDatePhotoId = (photoData as any)?.prev_date_photo_id;
  const nextDatePhotoId = (photoData as any)?.next_date_photo_id;
  const hasPrevPhoto = !!prevPhotoId;
  const hasNextPhoto = !!nextPhotoId;
  const hasPrevDate = !!(prevDatePhotoId && onNavigateToPhotoId);
  const hasNextDate = !!(nextDatePhotoId && onNavigateToPhotoId);

  // New hotspot objects dialog state
  const [showHotspotObjects, setShowHotspotObjects] = useState(false);
  const [currentHotspot, setCurrentHotspot] = useState<Hotspot | null>(null);
  // Refs
  const pannellumViewerRef = useRef<PannellumViewerHandle | null>(null);
  const recoveryAttemptedRef = useRef(false);

  useEffect(() => {
    recoveryAttemptedRef.current = false;
  }, [photoId]);

  const handlePanoramaError = useCallback(
    (_error: Error) => {
      // Pannellum's message can contain the complete signed URL, so do not
      // forward the raw error to the console.
      console.warn(LOG, 'Pannellum failed to load; refreshing the signed image URL.');
      if (recoveryAttemptedRef.current || !onPanoramaError) return;

      recoveryAttemptedRef.current = true;
      void onPanoramaError().catch((refreshError) => {
        console.error(LOG, 'Unable to refresh panorama image URL:', refreshError);
      });
    },
    [onPanoramaError]
  );

  const canonicalHotspots = useMemo<PanoramaHotspot[]>(
    () => toViewerPanoramaHotspots(hotspotsData?.hotspots ?? [], photoId),
    [hotspotsData, photoId]
  );
  // Function to handle hotspot clicks - show objects dialog
  const handleHotspotClick = useCallback(
    (rawHotspot: any) => {
      const hotspot: Hotspot = {
        id: rawHotspot.id,
        pitch: rawHotspot.pitch,
        yaw: rawHotspot.yaw,
        hfov: rawHotspot.hfov || 100,
        overlay_count: rawHotspot.overlay_count || 0,
        created_by: rawHotspot.created_by || null,
        title: rawHotspot.title,
        photo_id: rawHotspot.photo_id || photoId,
        created_at: rawHotspot.created_at || new Date().toISOString(),
        updated_at: rawHotspot.updated_at || new Date().toISOString(),
        objects: rawHotspot.objects || [],
      };

      setCurrentHotspot(hotspot);
      setShowHotspotObjects(true);
    },
    [photoId]
  );
  const handleObjectDeleted = useCallback(
    async (_objectId: number, _objectType: string) => {
      try {
        if (onHotspotCreated) {
          await onHotspotCreated();
        }
      } catch (error) {
        console.error('Error deleting object:', error);
      }
    },
    [onHotspotCreated]
  );

  if (!imageUrl) {
    return (
      <div className="relative w-full h-full">
        <DialogTitle />
        <DialogDescription />
        <div className="flex items-center justify-center h-full bg-black">
          <div className="text-white text-lg">Loading panorama...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      <DialogTitle />
      <DialogDescription />

      {/* Panorama container */}
      <PanoramaShell
        containerId="panorama"
        sceneId={photoId}
        imageUrl={imageUrl}
        hotspots={canonicalHotspots}
        hotspotsEnabled
        target={targetCoordinates ? { ...targetCoordinates, duration: 1000 } : null}
        pitchOffset={PITCH_OFFSET}
        viewerRef={pannellumViewerRef}
        hotspotClassName="iv-panorama-hotspot custom-hotspot"
        renderHotspotElement={renderIronview360PanoramaHotspot}
        onHotspotClick={(hotspot) => handleHotspotClick(fromViewerPanoramaHotspot(hotspot))}
        onTargetReached={onTargetReached}
        onLoad={() => {
          recoveryAttemptedRef.current = false;
        }}
        onError={handlePanoramaError}
        suppressErrorMessage
        className="iv-panorama__container w-full h-full rounded-lg"
        overlay={
          <>
            <LayoutMapPreview
              photoId={photoId}
              markerNumber={markerNumber}
              layoutName={layoutName}
              layoutType={layoutType}
              layoutImageUrl={layoutImageUrl}
              layoutFlippedHorizontal={layoutFlippedHorizontal}
              layoutFlippedVertical={layoutFlippedVertical}
              layoutImageDimensions={layoutImageDimensions}
              photoMarkers={photoMarkers}
              onNavigateToPhotoId={onNavigateToPhotoId}
            />

            {onHotspotCreated && (
              <FABMenu
                pannellumViewerRef={pannellumViewerRef}
                photoId={photoId}
                projectId={projectId}
                onHotspotCreated={onHotspotCreated}
              />
            )}

            <div className="absolute bottom-5 right-5 bg-black/30 p-[15px] rounded-lg shadow-[0_2px_10px_rgba(0,0,0,0.1)] z-[1000]">
              <div className="mb-[15px]" data-tour-id="iv-panorama-photo-nav">
                <div className="font-bold text-2xl mb-[5px] block text-white">Photo:</div>
                <div className="text-lg mb-2 text-white">{photoName}</div>
                <div className="flex gap-[10px] items-center justify-center">
                  <button
                    onClick={() => prevPhotoId && onNavigateToPhotoId?.(prevPhotoId)}
                    disabled={!hasPrevPhoto}
                    className={`bg-[#f6b03e] ${PANORAMA_NAV_BUTTON_BOX} rounded-full p-[5px] border-none transition-opacity flex items-center justify-center ${
                      hasPrevPhoto ? 'opacity-100 cursor-pointer' : 'opacity-30 cursor-not-allowed'
                    }`}
                  >
                    <ChevronLeft className={`${PANORAMA_NAV_BUTTON_ICON} text-white`} />
                  </button>
                  <button
                    onClick={() => nextPhotoId && onNavigateToPhotoId?.(nextPhotoId)}
                    disabled={!hasNextPhoto}
                    className={`bg-[#f6b03e] ${PANORAMA_NAV_BUTTON_BOX} rounded-full p-[5px] border-none transition-opacity flex items-center justify-center ${
                      hasNextPhoto ? 'opacity-100 cursor-pointer' : 'opacity-30 cursor-not-allowed'
                    }`}
                  >
                    <ChevronRight className={`${PANORAMA_NAV_BUTTON_ICON} text-white`} />
                  </button>
                </div>
              </div>

              <div data-tour-id="iv-panorama-date-nav">
                <div className="font-bold text-2xl mb-[5px] block text-white">Date:</div>
                <div className="text-lg mb-2 text-white">{photoDate}</div>
                <div className="flex gap-[10px] items-center justify-center">
                  <button
                    onClick={() => hasPrevDate && onNavigateToPhotoId?.(prevDatePhotoId)}
                    disabled={!hasPrevDate}
                    className={`bg-[#000000] ${PANORAMA_NAV_BUTTON_BOX} rounded-full p-[5px] border-none transition-opacity flex items-center justify-center ${
                      hasPrevDate ? 'opacity-100 cursor-pointer' : 'opacity-30 cursor-not-allowed'
                    }`}
                  >
                    <ChevronLeft className={`${PANORAMA_NAV_BUTTON_ICON} text-white`} />
                  </button>
                  <button
                    onClick={() => hasNextDate && onNavigateToPhotoId?.(nextDatePhotoId)}
                    disabled={!hasNextDate}
                    className={`bg-[#000000] ${PANORAMA_NAV_BUTTON_BOX} rounded-full p-[5px] border-none transition-opacity flex items-center justify-center ${
                      hasNextDate ? 'opacity-100 cursor-pointer' : 'opacity-30 cursor-not-allowed'
                    }`}
                  >
                    <ChevronRight className={`${PANORAMA_NAV_BUTTON_ICON} text-white`} />
                  </button>
                </div>
              </div>
            </div>

            <HotspotDialog
              open={showHotspotObjects}
              hotspot={currentHotspot}
              projectId={projectId}
              onObjectDeleted={handleObjectDeleted}
              onObjectUpdated={onHotspotCreated}
              onClose={() => {
                setShowHotspotObjects(false);
                setCurrentHotspot(null);
              }}
              markerNumber={markerNumber}
              layoutImageUrl={layoutImageUrl}
              layoutFlippedHorizontal={layoutFlippedHorizontal}
              layoutFlippedVertical={layoutFlippedVertical}
              layoutImageDimensions={layoutImageDimensions}
              photoMarkers={photoMarkers}
            />
          </>
        }
      />
    </div>
  );
};

export default Ironview360PanoramaExperience;
